import DivisionSign from '@/assets/icons/division-sign.svg';
import EqualSign from '@/assets/icons/equal-sign.svg';
import { HSpacer, MenuItem, NumericInput, Select, Text, VSpacer } from '@/components/DesignSystem';
import { RewardsLoyaltyValueInput } from "@/components/shared/RewardsLoyaltyValueInput";
import { isPointCapBelowPointsAwarded } from "@/pages/Promotions/PromotionModal/Helpers";
import { PromotionInputs } from "@/pages/Promotions/PromotionModal/PromotionModal";
import { FormHelperText, Stack } from '@mui/material';
import { roundToFixed } from '@shared/utilities';
import { isNil } from 'lodash';
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';

interface WorkflowStep2Props {
  isEditMode?: boolean,
  onPromotionChanged: (promotion: PromotionInputs) => void,
  promotion: PromotionInputs,
  storedPointCap?: number | null,
}

type CapCalculationType = 'dollars' | 'points';

export const WorkflowStep2 = ({
  isEditMode,
  onPromotionChanged,
  promotion,
  storedPointCap,
}: WorkflowStep2Props) => {
  const { t } = useTranslation();
  let initialCapCalculationType: CapCalculationType | null = null;
  if (!isNil(promotion.dollarCap)) {
    initialCapCalculationType = 'dollars';
  } else if (!isNil(promotion.pointCap)) {
    initialCapCalculationType = 'points';
  }
  const [capCalculationType, setCapCalculationType] = useState<CapCalculationType | null>(
    initialCapCalculationType,
  );
  const isCapBelowPointsAwarded = isPointCapBelowPointsAwarded(promotion, storedPointCap);
  return (
    <>
      <RewardsLoyaltyValueInput
        disableUomSelector={isEditMode}
        disabled={isEditMode}
        maxPercentValue={100}
        onUpdate={(updates) => {
          onPromotionChanged(
            {
              ...promotion,
              ...updates,
              ...(updates.uom !== promotion.uom && {
                productIdsForEarningPoints: undefined,
                productIdsForRedeemingPoints: undefined,
              }),
            },
          );
        }}
        rewardType={promotion.rewardType}
        testID={`${promotion.id}-loyalty-rewards-input`}
        uom={promotion.uom}
        value={promotion.value}
        width="560px"
      />
      <VSpacer size="9" />
      <Text category="title-medium">
        {t($ => $.promotions.create.step2.capLabel)}
      </Text>
      <VSpacer size="4" />
      <Stack alignItems="flex-start" direction="row">
        <Select
          label={t($ => $.promotions.create.step2.calculateAs)}
          onChangeValue={(value) => {
            setCapCalculationType(value as CapCalculationType);
            onPromotionChanged(
              {
                ...promotion,
                dollarCap: null,
                pointCap: null,
              },
            );
          }}
          testID="cap-calculation-select"
          value={capCalculationType}
          width={150}
        >
          <MenuItem testID="dollars-menu-item" value="dollars">
            {t($ => $.promotions.create.step2.dollars)}
          </MenuItem>
          <MenuItem testID="points-menu-item" value="points">
            {t($ => $.promotions.create.step2.points)}
          </MenuItem>
        </Select>
        {!!capCalculationType && (
          <>
            <HSpacer size="3" />
            <Stack>
              <Stack alignItems="center" direction="row">
                <NumericInput
                  decimals={2}
                  error={isCapBelowPointsAwarded}
                  minValue={0}
                  onChangeNumber={(value) => {
                    let pointCap = null;
                    if (value) {
                      if (capCalculationType === 'dollars') {
                        pointCap = Number(roundToFixed(value / 1.265, 2));
                      } else {
                        pointCap = value;
                      }
                    }
                    onPromotionChanged(
                      {
                        ...promotion,
                        dollarCap: capCalculationType === 'dollars' ? (value ?? null) : null,
                        pointCap,
                      },
                    );
                  }}
                  placeholder={capCalculationType === 'points' ? '0.00' : '$0.00'}
                  prefix={capCalculationType === 'points' ? '' : '$'}
                  showFixedDecimals={capCalculationType === 'dollars'}
                  testID="cap-calculation-input"
                  value={promotion.dollarCap ?? promotion.pointCap ?? undefined}
                  width={139}
                />
                {capCalculationType === 'dollars' && (
                  <>
                    <HSpacer size="5"/>
                    <img alt="division sign" src={DivisionSign} />
                    <HSpacer size="4"/>
                    <Text category="body-large">
                      1.265
                    </Text>
                    <HSpacer size="4"/>
                    <img alt="equal sign" src={EqualSign} />
                    <HSpacer size="4"/>
                    {!isNil(promotion.dollarCap) && !isNil(promotion.pointCap) && (
                      <Text category="body-large">
                        {promotion.pointCap} {t($ => $.common.units.points)}
                      </Text>
                    )}
                  </>
                )}
              </Stack>
              {isCapBelowPointsAwarded && (
                <FormHelperText error>
                  {t($ => $.promotions.create.step2.pointsCapAlreadyExceeded)}
                </FormHelperText>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </>
  );
};
